import { useState } from "react";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import {
  Type, Image, MousePointerClick, Minus, Columns2, LayoutGrid, Tag,
  Eye, Pencil, Smartphone, Monitor, Plus, Trash2, GripVertical,
  ChevronUp, ChevronDown, Copy
} from "lucide-react";

const BLOCK_TYPES = [
  { type: "text", label: "Texte", icon: Type, default: { content: "" } },
  { type: "image", label: "Image", icon: Image, default: { url: "", alt: "", width: "100%" } },
  { type: "button", label: "Bouton", icon: MousePointerClick, default: { text: "Cliquez ici", url: "#", color: "#f7c935", textColor: "#0b0811", align: "center" } },
  { type: "divider", label: "Separateur", icon: Minus, default: { color: "#e2e8f0", thickness: 1 } },
  { type: "columns", label: "2 Colonnes", icon: Columns2, default: { left: "", right: "" } },
  { type: "spacer", label: "Espacement", icon: LayoutGrid, default: { height: 20 } },
];

const inputCls = "bg-[#0b0811] border-white/10 text-white placeholder:text-slate-600 text-sm";
const CAT_COLORS = { entreprise: "#f7c935", client: "#3b82f6", formateur: "#22c55e", stagiaire: "#8b5cf6", formation: "#f97316", document: "#ef4444", facturation: "#06b6d4" };

export function EmailEditor({ form, setForm, tags = [] }) {
  const [previewMode, setPreviewMode] = useState("desktop"); // desktop | mobile
  const [view, setView] = useState("edit"); // edit | preview
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [showTagPanel, setShowTagPanel] = useState(false);

  const blocks = form.blocks || [];
  const setBlocks = (b) => setForm({ ...form, blocks: b });

  const addBlock = (type) => {
    const def = BLOCK_TYPES.find(b => b.type === type);
    setBlocks([...blocks, { id: `blk_${Date.now()}`, type, ...def.default }]);
  };
  const removeBlock = (i) => { setBlocks(blocks.filter((_, idx) => idx !== i)); setSelectedBlock(null); };
  const moveBlock = (i, dir) => {
    const arr = [...blocks]; const ni = i + dir;
    if (ni < 0 || ni >= arr.length) return;
    [arr[i], arr[ni]] = [arr[ni], arr[i]];
    setBlocks(arr); setSelectedBlock(ni);
  };
  const updateBlock = (i, data) => { const u = [...blocks]; u[i] = { ...u[i], ...data }; setBlocks(u); };
  const duplicateBlock = (i) => { setBlocks([...blocks.slice(0, i + 1), { ...blocks[i], id: `blk_${Date.now()}` }, ...blocks.slice(i + 1)]); };

  const tagsByCategory = {};
  (tags || []).forEach(t => { if (!tagsByCategory[t.category]) tagsByCategory[t.category] = []; tagsByCategory[t.category].push(t); });

  return (
    <div className="flex flex-col h-full" data-testid="email-editor">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10 bg-[#161020]">
        <button onClick={() => setView("edit")} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${view === "edit" ? "bg-[#f7c935] text-[#0b0811]" : "text-slate-400 hover:text-white"}`} data-testid="email-edit-tab">
          <Pencil className="w-3.5 h-3.5 inline mr-1" /> Edition
        </button>
        <button onClick={() => setView("preview")} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${view === "preview" ? "bg-[#f7c935] text-[#0b0811]" : "text-slate-400 hover:text-white"}`} data-testid="email-preview-tab">
          <Eye className="w-3.5 h-3.5 inline mr-1" /> Apercu
        </button>
        {view === "preview" && (
          <div className="flex items-center gap-1 ml-2 bg-[#0b0811] rounded-lg p-0.5 border border-white/10">
            <button onClick={() => setPreviewMode("desktop")} className={`p-1 rounded ${previewMode === "desktop" ? "bg-white/10 text-white" : "text-slate-500"}`}><Monitor className="w-3.5 h-3.5" /></button>
            <button onClick={() => setPreviewMode("mobile")} className={`p-1 rounded ${previewMode === "mobile" ? "bg-white/10 text-white" : "text-slate-500"}`}><Smartphone className="w-3.5 h-3.5" /></button>
          </div>
        )}
        <div className="flex-1" />
        <button onClick={() => setShowTagPanel(!showTagPanel)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${showTagPanel ? "bg-blue-500/20 text-blue-400" : "text-slate-400 hover:text-white"}`} data-testid="email-tags-toggle">
          <Tag className="w-3.5 h-3.5 inline mr-1" /> Balises
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left: Block Palette (edit mode) */}
        {view === "edit" && (
          <div className="w-48 border-r border-white/10 bg-[#161020] p-3 overflow-y-auto">
            {/* General */}
            <div className="mb-4">
              <Input value={form.name || ""} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nom du modele" className={`${inputCls} mb-2`} data-testid="email-name" />
              <Input value={form.email_subject || ""} onChange={e => setForm({ ...form, email_subject: e.target.value })} placeholder="Objet de l'email" className={inputCls} data-testid="email-subject" />
            </div>
            <div className="text-[10px] font-bold text-slate-400 uppercase mb-2">Blocs</div>
            <div className="space-y-1.5">
              {BLOCK_TYPES.map(b => (
                <button key={b.type} onClick={() => addBlock(b.type)} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg bg-[#0b0811] border border-white/5 hover:border-white/15 transition-colors text-left" data-testid={`add-block-${b.type}`}>
                  <b.icon className="w-4 h-4 text-[#f7c935]" />
                  <span className="text-xs text-slate-300">{b.label}</span>
                </button>
              ))}
            </div>
            {/* Styles globaux */}
            <div className="mt-4 space-y-2">
              <div className="text-[10px] font-bold text-slate-400 uppercase">Styles</div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-500">Fond</span>
                <input type="color" value={form.bg_color || "#ffffff"} onChange={e => setForm({ ...form, bg_color: e.target.value })} className="w-6 h-6 rounded border border-white/10 cursor-pointer" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-500">Texte</span>
                <input type="color" value={form.text_color || "#0b0811"} onChange={e => setForm({ ...form, text_color: e.target.value })} className="w-6 h-6 rounded border border-white/10 cursor-pointer" />
              </div>
            </div>
          </div>
        )}

        {/* Center: Canvas */}
        <div className="flex-1 overflow-y-auto p-4" style={{ background: "#2a2035" }}>
          {view === "edit" ? (
            <div className="max-w-[600px] mx-auto rounded-xl overflow-hidden border border-white/10" style={{ background: form.bg_color || "#ffffff" }}>
              {/* Email subject header */}
              <div className="px-4 py-2 bg-[#161020] border-b border-white/10">
                <div className="text-[10px] text-slate-500">Objet: <span className="text-white">{form.email_subject || "(sans objet)"}</span></div>
              </div>
              {/* Blocks */}
              <div className="min-h-[200px]">
                {blocks.length === 0 && (
                  <div className="text-center py-12 text-sm text-gray-400">Glissez des blocs depuis le panneau gauche</div>
                )}
                {blocks.map((blk, i) => (
                  <div key={blk.id} onClick={() => setSelectedBlock(i)} className={`relative group ${selectedBlock === i ? "ring-2 ring-[#f7c935]" : ""}`}>
                    {/* Block controls */}
                    <div className="absolute -left-0 top-0 flex flex-col gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-10 bg-[#161020] rounded-r-lg">
                      <button onClick={e => { e.stopPropagation(); moveBlock(i, -1); }} className="p-0.5"><ChevronUp className="w-3 h-3 text-slate-400" /></button>
                      <button onClick={e => { e.stopPropagation(); moveBlock(i, 1); }} className="p-0.5"><ChevronDown className="w-3 h-3 text-slate-400" /></button>
                      <button onClick={e => { e.stopPropagation(); duplicateBlock(i); }} className="p-0.5"><Copy className="w-3 h-3 text-blue-400" /></button>
                      <button onClick={e => { e.stopPropagation(); removeBlock(i); }} className="p-0.5"><Trash2 className="w-3 h-3 text-red-400" /></button>
                    </div>
                    {/* Block render */}
                    <div className="px-6 py-3" style={{ color: form.text_color || "#0b0811" }}>
                      {blk.type === "text" && <Textarea value={blk.content} onChange={e => updateBlock(i, { content: e.target.value })} className="bg-transparent border-dashed border-gray-300 text-current text-sm min-h-[40px] resize-none" placeholder="Saisissez votre texte..." />}
                      {blk.type === "image" && (
                        <div className="text-center">
                          {blk.url ? <img src={blk.url} alt={blk.alt} style={{ width: blk.width || "100%", maxWidth: "100%" }} className="rounded inline-block" /> : <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg py-8"><Image className="w-8 h-8 text-gray-400 mx-auto" /><Input value={blk.url || ""} onChange={e => updateBlock(i, { url: e.target.value })} placeholder="URL de l'image" className="mt-2 max-w-xs mx-auto text-xs bg-white border-gray-200 text-gray-800" /></div>}
                        </div>
                      )}
                      {blk.type === "button" && (
                        <div style={{ textAlign: blk.align || "center" }}>
                          <Input value={blk.text} onChange={e => updateBlock(i, { text: e.target.value })} className="inline-block w-auto text-center font-bold rounded-lg px-6 py-2 text-sm border-0" style={{ background: blk.color, color: blk.textColor }} />
                          <Input value={blk.url || ""} onChange={e => updateBlock(i, { url: e.target.value })} placeholder="URL du lien" className="mt-1 text-xs bg-white/50 border-gray-200 text-gray-600 max-w-xs mx-auto" />
                        </div>
                      )}
                      {blk.type === "divider" && <hr style={{ borderColor: blk.color, borderWidth: blk.thickness }} />}
                      {blk.type === "columns" && (
                        <div className="grid grid-cols-2 gap-3">
                          <Textarea value={blk.left} onChange={e => updateBlock(i, { left: e.target.value })} className="bg-transparent border-dashed border-gray-300 text-current text-sm min-h-[60px] resize-none" placeholder="Colonne gauche" />
                          <Textarea value={blk.right} onChange={e => updateBlock(i, { right: e.target.value })} className="bg-transparent border-dashed border-gray-300 text-current text-sm min-h-[60px] resize-none" placeholder="Colonne droite" />
                        </div>
                      )}
                      {blk.type === "spacer" && <div style={{ height: blk.height }} className="bg-transparent" />}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Preview */
            <div className="mx-auto rounded-xl overflow-hidden shadow-xl" style={{ maxWidth: previewMode === "mobile" ? 375 : 600, background: form.bg_color || "#ffffff" }} data-testid="email-preview">
              {blocks.map((blk, i) => (
                <div key={blk.id} className="px-6 py-3" style={{ color: form.text_color || "#0b0811" }}>
                  {blk.type === "text" && <div className="text-sm whitespace-pre-wrap">{blk.content}</div>}
                  {blk.type === "image" && blk.url && <div className="text-center"><img src={blk.url} alt={blk.alt} style={{ width: blk.width || "100%", maxWidth: "100%" }} className="rounded inline-block" /></div>}
                  {blk.type === "button" && <div style={{ textAlign: blk.align || "center" }}><a href={blk.url || "#"} className="inline-block font-bold rounded-lg px-6 py-3 text-sm no-underline" style={{ background: blk.color, color: blk.textColor }}>{blk.text}</a></div>}
                  {blk.type === "divider" && <hr style={{ borderColor: blk.color, borderWidth: blk.thickness }} />}
                  {blk.type === "columns" && <div className="grid grid-cols-2 gap-3 text-sm"><div className="whitespace-pre-wrap">{blk.left}</div><div className="whitespace-pre-wrap">{blk.right}</div></div>}
                  {blk.type === "spacer" && <div style={{ height: blk.height }} />}
                </div>
              ))}
              {blocks.length === 0 && <div className="text-center py-12 text-sm text-gray-400">Aucun bloc</div>}
            </div>
          )}
        </div>

        {/* Right: Tags Panel */}
        {showTagPanel && (
          <div className="w-52 border-l border-white/10 bg-[#161020] overflow-y-auto p-3" data-testid="email-tags-panel">
            <div className="text-[10px] font-bold text-slate-400 uppercase mb-2">Balises</div>
            <div className="text-[9px] text-slate-600 mb-3">Copiez et collez dans vos textes</div>
            {Object.entries(tagsByCategory).map(([cat, catTags]) => (
              <div key={cat} className="mb-3">
                <div className="text-[9px] font-bold uppercase tracking-wider mb-1" style={{ color: CAT_COLORS[cat] || "#64748b" }}>{cat}</div>
                <div className="flex flex-wrap gap-1">
                  {catTags.map(t => (
                    <button key={t.key} onClick={() => navigator.clipboard.writeText(`{${t.key}}`)} className="text-[9px] px-2 py-0.5 rounded-full border transition-colors hover:opacity-80"
                      style={{ background: (CAT_COLORS[cat] || "#64748b") + "15", color: CAT_COLORS[cat] || "#64748b", borderColor: (CAT_COLORS[cat] || "#64748b") + "30" }}
                      title={`${t.label} - Cliquer pour copier`} data-testid={`email-tag-${t.key}`}>
                      {"{" + t.key + "}"}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
